/**
 * POST /questions/user_profile
 */

import { RefinementCtx, z } from 'zod';

import { ZodEmploymentStatus } from '@Common/types/Employment';
import {
  QUESTIONS_ERROR_DOC_ALREADY_UPLOADED,
  QUESTIONS_ERROR_INVALID_ANNUAL_INDEX,
  QUESTIONS_ERROR_INVALID_FREE_STOCK,
} from '@Common/types/error_messages';
import { ZodFundingSource } from '@Common/types/Funding';
import { ZodLatestEmployerIndustryType } from '@Common/types/questions/employer_industry';
import { ZodNatureOfTransactions } from '@Common/types/questions/nature_of_transactions';
import { ZodCity, ZodFlatBuilding, ZodStreet } from '@Common/types/user/address';
import { ZodUserBirthdate } from '@Common/types/user/birthdate';
import { ZodUserFirstName, ZodUserLastName } from '@Common/types/user/legal_name';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';
import { validateNationalIdentifier } from '@Common/validators/userIdentifiersValidation';

export const ZodAddressInfo = z.object({
  postalcode: ZodTrimmedString,
  street: ZodStreet,
  flat_building: ZodFlatBuilding.nullable().optional(),
  city: ZodCity,
});

export type AddressInfo = z.infer<typeof ZodAddressInfo>;

export const reasonOfOpeningAccountValues = [
  'hedging',
  'income_earning',
  'other',
  'savings',
  'speculative_trading',
] as const;

export const ZodReasonOfOpeningAccount = z.enum(reasonOfOpeningAccountValues);

export type ReasonOfOpeningAccountType = z.infer<typeof ZodReasonOfOpeningAccount>;

export const getPriorityNationality = (nationality: string, otherNationality?: string): string => {
  const nationalities = [nationality, otherNationality]
    .filter((x) => x !== undefined)
    .sort((a, b) => a.localeCompare(b));
  return nationalities[0];
};

export const customNationalityValidation = (
  body: {
    nationality?: string;
    other_nationality?: string | null;
    national_identifier?: string | null;
  },
  ctx: RefinementCtx,
) => {
  const { nationality, other_nationality, national_identifier } = body;
  if (!national_identifier || !nationality) {
    return;
  }

  const nationalityCode = getPriorityNationality(nationality, other_nationality || undefined);

  const error = validateNationalIdentifier(nationalityCode, national_identifier);

  if (error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `"national_identifier" ${error}`,
    });

    return;
  }
};

export const ZodUserProfileNationalityBase = z.object({
  nationality: ZodCountryCode,
  other_nationality: ZodCountryCode.or(z.literal('')).optional().nullable(),
  national_identifier: ZodTrimmedString.nullable().optional(),
});

export const ZodUserProfileNationality: z.ZodType<z.infer<typeof ZodUserProfileNationalityBase>> =
  ZodUserProfileNationalityBase.superRefine(customNationalityValidation);

export const ZodPostCreateUserProfileBodyBase = z
  .object({
    firstname: ZodUserFirstName.optional(),
    lastname: ZodUserLastName.optional(),
    birthdate: ZodUserBirthdate.optional(),
    nationality: ZodCountryCode,
    other_nationality: ZodCountryCode.or(z.literal('')).optional().nullable(),
    national_identifier: ZodTrimmedString.nullable().optional(),
    annual_index: z.number().int(),
    invest_index: z.number().int(),
    estimated_net_worth_index: z.number().int().optional(),
    funding_source: ZodFundingSource,
    employment_status: ZodEmploymentStatus,
    is_control_person: z.boolean(),
    is_politically_exposed: z.boolean(),
    is_politically_exposed_relative: z.boolean(),
    employer_industry: ZodLatestEmployerIndustryType.optional(),
    job_title: ZodTrimmedString.optional(),
    reason_of_opening_account: ZodReasonOfOpeningAccount.optional(),
    nature_of_transactions: ZodNatureOfTransactions.optional(),
    mailing_address: ZodAddressInfo.optional(),
  })
  .merge(ZodAddressInfo)
  .merge(ZodUserProfileNationalityBase);

export const ZodPostCreateUserProfileBody = ZodPostCreateUserProfileBodyBase.superRefine(
  customNationalityValidation,
);

export type PostCreateUserProfileBody = z.infer<typeof ZodPostCreateUserProfileBody>;

export const ZodPostCreateUserProfileResponse = z.object({
  success: z.boolean(),
});

export type PostCreateUserProfileResponse = z.infer<typeof ZodPostCreateUserProfileResponse>;

export type PostCreateUserProfileErrorResponse = {
  error:
    | typeof QUESTIONS_ERROR_INVALID_ANNUAL_INDEX
    | typeof QUESTIONS_ERROR_INVALID_FREE_STOCK
    | typeof QUESTIONS_ERROR_DOC_ALREADY_UPLOADED;
};

/**
 * POST /questions/user_profile
 */

export const postUserProfileEndpoint = endpoint({
  method: 'POST',
  body: ZodPostCreateUserProfileBody,
  responseBody: ZodPostCreateUserProfileResponse,
});

export type PostCreateUserProfileBodyEndpoint = GetEndpointType<typeof postUserProfileEndpoint>;

/**
 * POST /questions/validate_national_identifier
 */

export const ZodPostValidateNationalIdentifierBody = z.object({
  nationality: ZodCountryCode,
  national_identifier: ZodTrimmedString,
  other_nationality: ZodCountryCode.or(z.literal('')).optional().nullable(),
});

export type PostValidateNationalIdentifierBody = z.infer<
  typeof ZodPostValidateNationalIdentifierBody
>;

export const ZodPostValidateNationalIdentifierResponse = z.object({
  isValid: z.boolean(),
});

export type PostValidateNationalIdentifierResponse = z.infer<
  typeof ZodPostValidateNationalIdentifierResponse
>;

export const postValidateNationalIdentifierEndpoint = endpoint({
  method: 'POST',
  body: ZodPostValidateNationalIdentifierBody,
  responseBody: ZodPostValidateNationalIdentifierResponse,
});

export type PostValidateNationalIdentifierEndpoint = GetEndpointType<
  typeof postValidateNationalIdentifierEndpoint
>;
